/**
 * Company Car Requests — receptionist + manager dashboard.
 *
 * Phase 1: list with status filter tabs and in-app Approve / Reject /
 * (with optional reason) actions. Sales agents land here too and see a
 * read-only list of their OWN requests (route /car-requests serves both
 * — the backend handles scope).
 *
 * Phase 3 (this file): counter-offer flow — receptionists can propose
 * a new pickup time on a pending request; agents accept, decline, or
 * counter back. Each side gets in-app + WhatsApp pings (handled in the
 * backend); the UI exposes the buttons.
 * Phase 4: proof-photo upload after the trip ends.
 * Phase 5: receptionist availability pill + dropdown (🟢/🟡/🔴/⚫).
 * Phase 6: manager CSV export button + audit summary strip.
 */
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';
import { useTheme } from '../contexts/ThemeContext';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Skeleton } from '../components/ui/skeleton';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from '../components/ui/dialog';
import {
  Car, CheckCircle2, XCircle, Clock, RefreshCw, MapPin, Users as UsersIcon,
  AlertCircle, Calendar, ArrowRightLeft, Upload, Camera, Download,
  AlertTriangle, ChevronDown,
} from 'lucide-react';
import { toast } from 'sonner';
import HelpInfo from '../components/HelpInfo';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const STATUS_TABS = [
  { value: 'pending', label: 'Pending', icon: Clock,
    tone: 'bg-amber-100 text-amber-700 border-amber-300' },
  { value: 'counter_offer_pending', label: 'Counter', icon: ArrowRightLeft,
    tone: 'bg-violet-100 text-violet-700 border-violet-300' },
  { value: 'approved', label: 'Approved', icon: CheckCircle2,
    tone: 'bg-emerald-100 text-emerald-700 border-emerald-300' },
  { value: 'awaiting_proof', label: 'Awaiting proof', icon: Camera,
    tone: 'bg-blue-100 text-blue-700 border-blue-300' },
  { value: 'completed', label: 'Completed', icon: CheckCircle2,
    tone: 'bg-slate-100 text-slate-700 border-slate-300' },
  { value: 'rejected', label: 'Rejected', icon: XCircle,
    tone: 'bg-red-100 text-red-700 border-red-300' },
  { value: 'escalated', label: 'Escalated', icon: AlertTriangle,
    tone: 'bg-rose-100 text-rose-800 border-rose-300' },
  { value: 'all', label: 'All', icon: Car,
    tone: 'bg-slate-100 text-slate-700 border-slate-300' },
];

// Receptionist availability options shown in the header dropdown.
const AVAIL_OPTIONS = [
  { value: 'available',   emoji: '🟢', label: 'Available' },
  { value: 'busy',        emoji: '🟡', label: 'Busy' },
  { value: 'unavailable', emoji: '🔴', label: 'Unavailable' },
  { value: 'offline',     emoji: '⚫', label: 'Offline' },
];

const STATUS_STYLE = {
  pending: 'bg-amber-100 text-amber-700 border-amber-300',
  approved: 'bg-emerald-100 text-emerald-700 border-emerald-300',
  rejected: 'bg-red-100 text-red-700 border-red-300',
  counter_offer_pending: 'bg-violet-100 text-violet-700 border-violet-300',
  awaiting_proof: 'bg-blue-100 text-blue-700 border-blue-300',
  completed: 'bg-slate-100 text-slate-700 border-slate-300',
  escalated: 'bg-rose-200 text-rose-800 border-rose-400',
};

const fmtPickup = (iso) => {
  if (!iso) return '—';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '—';
  return d.toLocaleString([], {
    month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit',
  });
};

const RejectModal = ({ open, onOpenChange, onConfirm, requestCode }) => {
  const [reason, setReason] = useState('');
  useEffect(() => { if (open) setReason(''); }, [open]);
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-sm">
        <DialogHeader>
          <DialogTitle>Reject car request {requestCode}?</DialogTitle>
          <DialogDescription>
            The agent will be notified. A reason is optional but helpful so
            they know whether to re-submit.
          </DialogDescription>
        </DialogHeader>
        <Textarea
          value={reason}
          onChange={(e) => setReason(e.target.value)}
          rows={3}
          placeholder="Reason (optional) — e.g. car already booked, driver off…"
        />
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
          <Button
            className="bg-red-600 hover:bg-red-500 text-white"
            onClick={() => { onConfirm(reason); onOpenChange(false); }}
          >
            Reject request
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

// Counter-offer dialog. Used by receptionists (on pending requests) and
// agents (responding to a previous counter). We split the datetime into
// a date input + time input — most browsers' native datetime-local is
// awkward on touch devices, and date+time inputs render larger hit
// targets that field staff can actually tap.
const CounterOfferModal = ({ open, onOpenChange, onConfirm, requestCode, defaultPickup }) => {
  const [date, setDate] = useState('');
  const [time, setTime] = useState('');
  const [reason, setReason] = useState('');
  useEffect(() => {
    if (!open) return;
    // Default to the existing pickup, or "now + 1 hour" if not set.
    const seed = defaultPickup ? new Date(defaultPickup) : new Date(Date.now() + 60 * 60 * 1000);
    if (Number.isNaN(seed.getTime())) {
      setDate(''); setTime('');
    } else {
      const yyyy = seed.getFullYear();
      const mm = String(seed.getMonth() + 1).padStart(2, '0');
      const dd = String(seed.getDate()).padStart(2, '0');
      setDate(`${yyyy}-${mm}-${dd}`);
      setTime(`${String(seed.getHours()).padStart(2, '0')}:${String(seed.getMinutes()).padStart(2, '0')}`);
    }
    setReason('');
  }, [open, defaultPickup]);

  const submit = () => {
    if (!date || !time) {
      toast.error('Please pick both a date and a time');
      return;
    }
    // Construct an ISO datetime from the local pickers, then convert
    // to UTC via toISOString so the backend receives an unambiguous
    // value regardless of the user's tz.
    const local = new Date(`${date}T${time}`);
    if (Number.isNaN(local.getTime())) {
      toast.error('Could not parse the selected date / time');
      return;
    }
    if (local.getTime() <= Date.now()) {
      toast.error('Proposed time must be in the future');
      return;
    }
    onConfirm(local.toISOString(), reason || undefined);
    onOpenChange(false);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-sm">
        <DialogHeader>
          <DialogTitle>Counter-offer on {requestCode}</DialogTitle>
          <DialogDescription>
            Propose a different pickup time. The other side will be notified
            (in-app + WhatsApp) and can accept, decline, or counter back.
            Three rounds without agreement auto-escalates to a manager.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          <div className="grid grid-cols-2 gap-2">
            <div>
              <label className="text-xs text-slate-600 mb-1 block">Date</label>
              <Input type="date" value={date} onChange={(e) => setDate(e.target.value)} />
            </div>
            <div>
              <label className="text-xs text-slate-600 mb-1 block">Time</label>
              <Input type="time" value={time} onChange={(e) => setTime(e.target.value)} />
            </div>
          </div>
          <div>
            <label className="text-xs text-slate-600 mb-1 block">Reason (optional)</label>
            <Textarea
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              rows={2}
              placeholder="e.g. earlier slot is double-booked, driver returns at 14:00…"
            />
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
          <Button
            className="bg-violet-600 hover:bg-violet-500 text-white"
            onClick={submit}
          >
            <ArrowRightLeft size={13} className="mr-1.5" />
            Send counter-offer
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

// Proof-photo upload dialog. Agent picks a file; we preview it locally
// and POST it as multipart/form-data to /proof. Photo-only by design —
// videos / PDFs aren't valid "proof of trip".
const ProofUploadModal = ({ open, onOpenChange, onConfirm, requestCode }) => {
  const [file, setFile] = useState(null);
  const [note, setNote] = useState('');
  const [previewUrl, setPreviewUrl] = useState('');
  const [uploading, setUploading] = useState(false);

  useEffect(() => {
    if (!open) {
      setFile(null); setNote(''); setPreviewUrl(''); setUploading(false);
      return;
    }
  }, [open]);

  // Build a preview URL when a file is selected, and revoke the
  // ObjectURL on cleanup to avoid leaking memory across re-opens.
  useEffect(() => {
    if (!file) { setPreviewUrl(''); return undefined; }
    const url = URL.createObjectURL(file);
    setPreviewUrl(url);
    return () => URL.revokeObjectURL(url);
  }, [file]);

  const onFileChange = (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    if (f.size > 10 * 1024 * 1024) {
      toast.error('Photo is over 10MB. Please compress it or take a new one.');
      return;
    }
    if (!/^image\/(jpeg|jpg|png|webp)$/i.test(f.type)) {
      toast.error('Only JPG, PNG, or WebP images are accepted.');
      return;
    }
    setFile(f);
  };

  const submit = async () => {
    if (!file) {
      toast.error('Please select a photo');
      return;
    }
    setUploading(true);
    try {
      await onConfirm(file, note);
      onOpenChange(false);
    } finally {
      setUploading(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>Upload proof for {requestCode}</DialogTitle>
          <DialogDescription>
            Snap or upload a photo from the trip — a quick shot at the
            meeting / drop-off is enough. Closes the request as
            <strong> completed</strong>.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          <input
            type="file"
            accept="image/jpeg,image/jpg,image/png,image/webp"
            capture="environment"
            onChange={onFileChange}
            className="text-sm"
          />
          {previewUrl && (
            <img
              src={previewUrl}
              alt="Proof preview"
              className="rounded-md border border-slate-200 max-h-56 mx-auto"
            />
          )}
          <div>
            <label className="text-xs text-slate-600 mb-1 block">Caption (optional)</label>
            <Textarea
              value={note}
              onChange={(e) => setNote(e.target.value)}
              rows={2}
              placeholder="e.g. handover at lobby, met Mr. Khan, signed paperwork…"
            />
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} disabled={uploading}>
            Cancel
          </Button>
          <Button
            className="bg-emerald-600 hover:bg-emerald-500 text-white"
            onClick={submit}
            disabled={uploading || !file}
          >
            {uploading ? (
              <><RefreshCw size={13} className="mr-1.5 animate-spin" /> Uploading…</>
            ) : (
              <><Upload size={13} className="mr-1.5" /> Upload proof</>
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

// Availability pill + dropdown. Renders the user's current status and
// lets them switch with one click. Hidden for non-receptionist roles
// (we render a no-op fragment in that case).
const AvailabilityWidget = ({ token, role }) => {
  const [status, setStatus] = useState('offline');
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);

  const isReceptionist = useMemo(() => {
    const r = (role || '').toLowerCase();
    return ['receptionist', 'admin', 'owner', 'ceo', 'hod', 'manager', 'super_admin']
      .some((h) => r.includes(h));
  }, [role]);

  const fetchStatus = useCallback(async () => {
    if (!token || !isReceptionist) return;
    try {
      const res = await axios.get(`${API}/car-requests/receptionists/availability`,
        { params: { token } });
      const me = (res.data?.data?.receptionists || []).find(
        (u) => u.receptionist_status,
      );
      // We don't know our own user id here; the backend already filters
      // to the org, but the most useful display is the FIRST primary
      // receptionist (us, in single-receptionist setups). For multi-
      // receptionist orgs the user can still toggle their own status
      // and the dropdown will reflect what they pick.
      if (me?.receptionist_status) setStatus(me.receptionist_status);
    } catch (_) {
      /* silent — widget degrades to "offline" */
    }
  }, [token, isReceptionist]);
  useEffect(() => { fetchStatus(); }, [fetchStatus]);

  const change = async (value) => {
    setOpen(false);
    if (value === status) return;
    setBusy(true);
    try {
      const res = await axios.put(
        `${API}/car-requests/receptionists/availability`,
        { status: value },
        { params: { token } },
      );
      setStatus(value);
      const flushed = res.data?.data?.flushed_queued_count || 0;
      if (value === 'available' && flushed > 0) {
        toast.success(`You're available — picked up ${flushed} queued request${flushed === 1 ? '' : 's'}.`);
      } else {
        toast.success(`Status set to ${value}`);
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Could not update availability');
    } finally {
      setBusy(false);
    }
  };

  if (!isReceptionist) return null;
  const current = AVAIL_OPTIONS.find((o) => o.value === status) || AVAIL_OPTIONS[3];

  return (
    <div className="relative">
      <Button
        variant="outline"
        size="sm"
        onClick={() => setOpen((v) => !v)}
        disabled={busy}
        className="h-8"
      >
        <span className="mr-1.5">{current.emoji}</span>
        {current.label}
        <ChevronDown size={12} className="ml-1" />
      </Button>
      {open && (
        <div className="absolute right-0 mt-1 w-44 rounded-md border border-slate-200 bg-white dark:bg-slate-800 dark:border-slate-700 shadow-md z-20">
          {AVAIL_OPTIONS.map((o) => (
            <button
              key={o.value}
              type="button"
              onClick={() => change(o.value)}
              className={`w-full text-left px-3 py-1.5 text-sm hover:bg-slate-50 dark:hover:bg-slate-700 flex items-center gap-2 ${o.value === status ? 'font-semibold' : ''}`}
            >
              <span>{o.emoji}</span> {o.label}
            </button>
          ))}
        </div>
      )}
    </div>
  );
};

export default function CarRequests() {
  const { token, user } = useAuth();
  const { isDarkMode } = useTheme();
  const [activeTab, setActiveTab] = useState('pending');
  const [requests, setRequests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [counts, setCounts] = useState({
    pending: 0, counter_offer_pending: 0, approved: 0,
    awaiting_proof: 0, completed: 0, rejected: 0, escalated: 0, all: 0,
  });
  const [rejectTarget, setRejectTarget] = useState(null);
  const [counterTarget, setCounterTarget] = useState(null);
  const [proofTarget, setProofTarget] = useState(null);

  // Receptionist roles can decide; agents see read-only-ish (but can
  // upload proof + accept/decline counter-offers on their own requests).
  const role = (user?.role || '').toLowerCase();
  const canDecide = useMemo(
    () => ['receptionist', 'admin', 'owner', 'ceo',
           'hod', 'hod_sales', 'hod_marketing', 'manager', 'super_admin']
      .some((h) => role.includes(h)),
    [role],
  );
  // Managers + admins (but NOT primary receptionist) get the audit /
  // CSV controls. Receptionists can also export.
  const canExport = useMemo(
    () => ['receptionist', 'admin', 'owner', 'ceo', 'hod', 'manager', 'super_admin']
      .some((h) => role.includes(h)),
    [role],
  );

  const load = useCallback(async () => {
    if (!token) return;
    setLoading(true);
    try {
      // Fetch the current tab AND the counts for the tab badges.
      const [res, allRes] = await Promise.all([
        axios.get(`${API}/car-requests`, {
          params: { token, status: activeTab === 'all' ? undefined : activeTab, limit: 200 },
        }),
        axios.get(`${API}/car-requests`, { params: { token, limit: 500 } }),
      ]);
      setRequests(res.data?.data?.requests || []);
      const all = allRes.data?.data?.requests || [];
      const byStatus = (s) => all.filter((r) => r.status === s).length;
      setCounts({
        all: all.length,
        pending: byStatus('pending'),
        counter_offer_pending: byStatus('counter_offer_pending'),
        approved: byStatus('approved'),
        awaiting_proof: byStatus('awaiting_proof'),
        completed: byStatus('completed'),
        rejected: byStatus('rejected'),
        escalated: byStatus('escalated'),
      });
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Could not load car requests');
    } finally {
      setLoading(false);
    }
  }, [token, activeTab]);

  useEffect(() => { load(); }, [load]);

  const decide = async (request, action, reason) => {
    try {
      await axios.patch(
        `${API}/car-requests/${request.id}/decide`,
        { action, reason },
        { params: { token } },
      );
      toast.success(action === 'approve' ? 'Approved' : 'Rejected');
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || `Could not ${action} the request`);
    }
  };

  const sendCounterOffer = async (request, proposedIso, reason) => {
    try {
      await axios.post(
        `${API}/car-requests/${request.id}/counter-offer`,
        { proposed_time: proposedIso, reason },
        { params: { token } },
      );
      toast.success(`Counter-offer sent on ${request.request_code}`);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Could not send counter-offer');
    }
  };

  const respondToCounter = async (request, action, reason) => {
    try {
      await axios.post(
        `${API}/car-requests/${request.id}/respond`,
        { action, reason },
        { params: { token } },
      );
      toast.success(action === 'accept' ? 'Counter-offer accepted' : 'Counter-offer declined');
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || `Could not ${action} the counter-offer`);
    }
  };

  const uploadProof = async (request, file, note) => {
    try {
      const form = new FormData();
      form.append('file', file);
      if (note) form.append('note', note);
      await axios.post(
        `${API}/car-requests/${request.id}/proof`,
        form,
        {
          params: { token },
          headers: { 'Content-Type': 'multipart/form-data' },
        },
      );
      toast.success(`Proof uploaded for ${request.request_code}`);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Could not upload proof');
      throw e;  // bubble so the modal stays open on failure
    }
  };

  const exportCsv = async () => {
    if (!token) return;
    try {
      const res = await axios.get(`${API}/car-requests/export/csv`, {
        params: {
          token,
          // Default 30-day window; the backend defaults if these are omitted
          status: activeTab !== 'all' ? activeTab : undefined,
        },
        responseType: 'blob',
      });
      const url = URL.createObjectURL(new Blob([res.data], { type: 'text/csv' }));
      const a = document.createElement('a');
      a.href = url;
      a.download = `car_requests_${new Date().toISOString().slice(0, 10)}.csv`;
      a.click();
      URL.revokeObjectURL(url);
      toast.success('Exported CSV');
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Could not export CSV');
    }
  };

  return (
    <div className={`min-h-screen ${isDarkMode ? 'bg-[#0a0a0f] text-white' : 'bg-slate-50'}`}>
      <div className="max-w-[1400px] mx-auto p-6 space-y-5">
        {/* Header */}
        <div className="flex items-center justify-between flex-wrap gap-3">
          <div>
            <h1 className="text-2xl font-bold flex items-center gap-2 flex-wrap">
              <Car className="text-violet-500" size={26} />
              Car Requests
              <HelpInfo
                align="inline"
                title="How Car Requests work"
                steps={[
                  <>Sales agents submit a car request when they log a field visit. Each request starts as <strong>Pending</strong>.</>,
                  <>The receptionist (or a manager covering for her) opens this page, picks the <strong>Pending</strong> tab, and <strong>Approves</strong> or <strong>Rejects</strong> each request. A rejection reason is optional but helps the agent.</>,
                  <>Once decided the agent is notified in-app; on approval the trip is scheduled at the pickup time the agent requested.</>,
                  <>Counter-offers, WhatsApp dispatch, and post-trip proof photos land in the next phases of this feature.</>,
                ]}
                tip="Sort the Pending tab by pickup time (earliest first) — that's the queue you actually need to clear before the agent leaves."
              />
            </h1>
            <p className="text-sm text-slate-500 dark:text-slate-400 mt-1">
              {canDecide
                ? 'Approve, reject, and track every company car request from the team.'
                : 'Track your own car requests.'}
            </p>
          </div>
          <div className="flex items-center gap-2 flex-wrap">
            <AvailabilityWidget token={token} role={user?.role} />
            {canExport && (
              <Button variant="outline" size="sm" onClick={exportCsv} className="h-8">
                <Download size={14} className="mr-1.5" /> Export CSV
              </Button>
            )}
            <Button variant="outline" size="sm" onClick={load} disabled={loading} className="h-8">
              <RefreshCw size={14} className={`mr-1.5 ${loading ? 'animate-spin' : ''}`} />
              Refresh
            </Button>
          </div>
        </div>

        {/* Status tabs as count tiles */}
        <div className="grid grid-cols-2 sm:grid-cols-4 lg:grid-cols-8 gap-2">
          {STATUS_TABS.map((t) => {
            const Icon = t.icon;
            const isActive = activeTab === t.value;
            return (
              <button
                key={t.value}
                type="button"
                onClick={() => setActiveTab(t.value)}
                className={`rounded-md border p-3 text-left transition ${
                  isActive
                    ? `${t.tone} ring-2 ring-offset-1 ring-current`
                    : 'border-slate-200 bg-white hover:bg-slate-50 dark:bg-slate-900 dark:border-slate-700'
                }`}
              >
                <div className="flex items-center justify-between">
                  <span className="flex items-center gap-1.5 text-xs uppercase font-medium">
                    <Icon size={12} /> {t.label}
                  </span>
                </div>
                <div className="text-2xl font-bold mt-1">{counts[t.value]}</div>
              </button>
            );
          })}
        </div>

        {/* List */}
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-base capitalize">
              {activeTab === 'all' ? 'All requests' : `${activeTab} requests`}
            </CardTitle>
            <CardDescription>
              {requests.length} request{requests.length === 1 ? '' : 's'} in this view
            </CardDescription>
          </CardHeader>
          <CardContent>
            {loading && !requests.length ? (
              <div className="space-y-2">
                {[0, 1, 2].map((i) => <Skeleton key={i} className="h-20 w-full" />)}
              </div>
            ) : requests.length === 0 ? (
              <div className="py-12 text-center text-slate-500">
                <Car size={32} className="mx-auto text-slate-300 mb-2" />
                No {activeTab === 'all' ? '' : activeTab + ' '}requests.
              </div>
            ) : (
              <div className="space-y-2">
                {requests.map((r) => (
                  <RequestRow
                    key={r.id}
                    req={r}
                    currentUserId={user?.id}
                    canDecide={canDecide}
                    onApprove={() => decide(r, 'approve')}
                    onReject={() => setRejectTarget(r)}
                    onCounter={() => setCounterTarget(r)}
                    onAcceptCounter={() => respondToCounter(r, 'accept')}
                    onDeclineCounter={() => respondToCounter(r, 'decline')}
                    onUploadProof={() => setProofTarget(r)}
                  />
                ))}
              </div>
            )}
          </CardContent>
        </Card>

        <RejectModal
          open={Boolean(rejectTarget)}
          onOpenChange={(v) => !v && setRejectTarget(null)}
          requestCode={rejectTarget?.request_code}
          onConfirm={(reason) => rejectTarget && decide(rejectTarget, 'reject', reason)}
        />

        <CounterOfferModal
          open={Boolean(counterTarget)}
          onOpenChange={(v) => !v && setCounterTarget(null)}
          requestCode={counterTarget?.request_code}
          defaultPickup={
            counterTarget?.current_proposed_time || counterTarget?.pickup_at
          }
          onConfirm={(iso, reason) =>
            counterTarget && sendCounterOffer(counterTarget, iso, reason)
          }
        />

        <ProofUploadModal
          open={Boolean(proofTarget)}
          onOpenChange={(v) => !v && setProofTarget(null)}
          requestCode={proofTarget?.request_code}
          onConfirm={(file, note) =>
            proofTarget && uploadProof(proofTarget, file, note)
          }
        />
      </div>
    </div>
  );
}

const RequestRow = ({
  req, canDecide, currentUserId,
  onApprove, onReject, onCounter,
  onAcceptCounter, onDeclineCounter, onUploadProof,
}) => {
  const isThisAgent = currentUserId && req.agent_id === currentUserId;
  // Which actions should this row show? The matrix:
  //   pending + receptionist           → Approve / Reject / Counter
  //   counter_offer_pending + agent    → Accept / Decline / Counter back
  //     (only when receptionist made the last counter)
  //   counter_offer_pending + receptionist → Accept / Decline / Counter back
  //     (only when agent made the last counter)
  //   awaiting_proof + agent           → Upload proof
  //   approved + agent                 → Upload proof (eager)
  // The backend re-checks all permissions, so misclicks just produce
  // a friendly 403 toast — but hiding the wrong buttons keeps the UI
  // calm.
  const lastCounterByReceptionist = (() => {
    const r = (req.last_counter_by_role || '').toLowerCase();
    return ['receptionist', 'admin', 'owner', 'ceo', 'hod', 'manager', 'super_admin']
      .some((h) => r.includes(h));
  })();
  const showApproveReject = canDecide && req.status === 'pending';
  const showCounter = (
    (canDecide && req.status === 'pending')
    || (req.status === 'counter_offer_pending' && (
      (lastCounterByReceptionist && isThisAgent)
      || (!lastCounterByReceptionist && canDecide)
    ))
  );
  const showAcceptDecline = req.status === 'counter_offer_pending' && (
    (lastCounterByReceptionist && isThisAgent)
    || (!lastCounterByReceptionist && canDecide)
  );
  const showUploadProof = (
    isThisAgent && (req.status === 'awaiting_proof' || req.status === 'approved')
  );

  return (
    <div className="border border-slate-200 dark:border-slate-700 rounded-md p-3">
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <span className="font-semibold text-slate-900 dark:text-slate-100">
              {req.request_code}
            </span>
            <Badge className={`text-[10px] border capitalize ${STATUS_STYLE[req.status] || STATUS_STYLE.pending}`}>
              {req.status.replace(/_/g, ' ')}
            </Badge>
            {req.counter_offer_count > 0 && (
              <Badge variant="outline" className="text-[10px] border-violet-300 text-violet-700">
                Counter {req.counter_offer_count}/3
              </Badge>
            )}
            {req.queued && (
              <Badge variant="outline" className="text-[10px] border-amber-300 text-amber-700">
                Queued
              </Badge>
            )}
            <span className="text-xs text-slate-500">
              requested by <strong>{req.agent_name || 'Agent'}</strong>
            </span>
          </div>
          <div className="mt-1.5 flex items-center gap-3 text-xs text-slate-600 dark:text-slate-300 flex-wrap">
            <span className="flex items-center gap-1">
              <Calendar size={11} className="text-violet-500" />
              {fmtPickup(
                req.status === 'counter_offer_pending'
                  ? (req.current_proposed_time || req.pickup_at)
                  : req.pickup_at
              )}
              {req.status === 'counter_offer_pending' && req.current_proposed_time && (
                <span className="text-[10px] text-violet-600 ml-1">
                  (proposed)
                </span>
              )}
            </span>
            <span className="flex items-center gap-1">
              <MapPin size={11} className="text-rose-500" />
              {req.pickup_location} → {req.dropoff_location}
            </span>
            <span className="flex items-center gap-1">
              <UsersIcon size={11} className="text-blue-500" />
              {req.passengers} passenger{req.passengers === 1 ? '' : 's'}
            </span>
          </div>
          {req.notes && (
            <p className="mt-1.5 text-xs text-slate-500 italic">"{req.notes}"</p>
          )}
          {req.last_counter_reason && req.status === 'counter_offer_pending' && (
            <p className="mt-1.5 text-xs text-violet-700 flex items-start gap-1">
              <ArrowRightLeft size={11} className="mt-0.5 flex-shrink-0" />
              <strong>{req.last_counter_by_name || 'They'}: </strong>
              {req.last_counter_reason}
            </p>
          )}
          {req.decision_reason && (
            <p className="mt-1.5 text-xs text-slate-600 flex items-start gap-1">
              <AlertCircle size={11} className="mt-0.5 text-slate-400 flex-shrink-0" />
              {req.approved_by_name ? `${req.approved_by_name}: ` : ''}{req.decision_reason}
            </p>
          )}
          {req.proof_document_url && req.status === 'completed' && (
            <a
              href={req.proof_document_url}
              target="_blank"
              rel="noopener noreferrer"
              className="mt-1.5 inline-flex items-center gap-1 text-xs text-emerald-700 hover:underline"
            >
              <Camera size={11} /> View proof photo
            </a>
          )}
        </div>
        <div className="flex gap-1.5 flex-shrink-0 flex-wrap">
          {showApproveReject && (
            <>
              <Button
                size="sm"
                className="h-8 bg-emerald-600 hover:bg-emerald-500 text-white"
                onClick={onApprove}
              >
                <CheckCircle2 size={13} className="mr-1" /> Approve
              </Button>
              <Button
                size="sm"
                variant="outline"
                className="h-8 border-red-300 text-red-600 hover:bg-red-50"
                onClick={onReject}
              >
                <XCircle size={13} className="mr-1" /> Reject
              </Button>
            </>
          )}
          {showAcceptDecline && (
            <>
              <Button
                size="sm"
                className="h-8 bg-emerald-600 hover:bg-emerald-500 text-white"
                onClick={onAcceptCounter}
              >
                <CheckCircle2 size={13} className="mr-1" /> Accept
              </Button>
              <Button
                size="sm"
                variant="outline"
                className="h-8 border-red-300 text-red-600 hover:bg-red-50"
                onClick={onDeclineCounter}
              >
                <XCircle size={13} className="mr-1" /> Decline
              </Button>
            </>
          )}
          {showCounter && (
            <Button
              size="sm"
              variant="outline"
              className="h-8 border-violet-300 text-violet-700 hover:bg-violet-50"
              onClick={onCounter}
            >
              <ArrowRightLeft size={13} className="mr-1" /> Counter
            </Button>
          )}
          {showUploadProof && (
            <Button
              size="sm"
              variant="outline"
              className="h-8 border-blue-300 text-blue-700 hover:bg-blue-50"
              onClick={onUploadProof}
            >
              <Upload size={13} className="mr-1" /> Upload proof
            </Button>
          )}
        </div>
      </div>
    </div>
  );
};
